import React, { useState, useEffect } from 'react';
import { notifications } from '../utils/notifications';

export default function NotificationPreferences() {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const data = await notifications.getPreferences();
      setPreferences(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (channel) => {
    const updatedPreferences = preferences.map(pref =>
      pref.channel === channel ? { ...pref, enabled: !pref.enabled } : pref
    );
    
    setPreferences(updatedPreferences);
    setSaving(true);
    setMessage('');

    try {
      await notifications.updatePreferences(updatedPreferences);
      setMessage('Preferences updated successfully');
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      setMessage('Failed to update preferences');
      // Revert changes on error
      loadPreferences();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">Notification Preferences</h2>
      <div className="space-y-4">
        {preferences.map((pref) => (
          <div key={pref.channel} className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                {pref.channel === 'in_app' ? 'In-App Notifications' : 'Email Notifications'}
              </h3>
              <p className="text-sm text-gray-500">
                {pref.channel === 'in_app'
                  ? 'Receive notifications in the application'
                  : 'Receive notifications via email'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle(pref.channel)}
              disabled={saving}
              className={`${
                pref.enabled ? 'bg-indigo-600' : 'bg-gray-200'
              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
            >
              <span className="sr-only">Toggle {pref.channel} notifications</span>
              <span
                className={`${
                  pref.enabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              >
                <span
                  className={`${
                    pref.enabled
                      ? 'opacity-0 ease-out duration-100'
                      : 'opacity-100 ease-in duration-200'
                  } absolute inset-0 h-full w-full flex items-center justify-center transition-opacity`}
                  aria-hidden="true"
                >
                  <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                    <path
                      d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span
                  className={`${
                    pref.enabled
                      ? 'opacity-100 ease-in duration-200'
                      : 'opacity-0 ease-out duration-100'
                  } absolute inset-0 h-full w-full flex items-center justify-center transition-opacity`}
                  aria-hidden="true"
                >
                  <svg
                    className="h-3 w-3 text-indigo-600"
                    fill="currentColor"
                    viewBox="0 0 12 12"
                  >
                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                  </svg>
                </span>
              </span>
            </button>
          </div>
        ))}
      </div>
      {message && (
        <div className={`mt-4 p-4 rounded-md ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}
    </div>
  );
}
